/** Port of SupabaseProgressRepository: remote row + local cache, upsert last-write-wins. */
import { LOCAL_PROGRESS_KEY, progressFromRow, progressToRow, type EpisodeContent, type EpisodeProgress, type ProgressRow } from '@/domain';
import { supabase } from './supabase';

function readLocal(episodeID: string): ProgressRow | undefined {
  try { const raw = localStorage.getItem(LOCAL_PROGRESS_KEY(episodeID)); return raw ? (JSON.parse(raw) as ProgressRow) : undefined; } catch { return undefined; }
}
function writeLocal(row: ProgressRow) {
  try { localStorage.setItem(LOCAL_PROGRESS_KEY(row.episode_id), JSON.stringify(row)); } catch { /* ignore */ }
}

/**
 * Progress row only (no episode content needed), so it can be fetched in parallel with the episode.
 * Remote error → the local cache row (offline), none → undefined. Map it with `progressFromRow`.
 */
export async function loadProgressRow(userID: string, episodeID: string): Promise<ProgressRow | undefined> {
  const { data, error } = await supabase().from('player_episode_progress').select('*').eq('user_id', userID).eq('episode_id', episodeID).limit(1).maybeSingle();
  if (error) return readLocal(episodeID);
  return (data as ProgressRow | null) ?? undefined;
}

export async function loadProgress(userID: string, episode: EpisodeContent): Promise<EpisodeProgress | undefined> {
  const row = await loadProgressRow(userID, episode.id);
  return row ? progressFromRow(row, episode) : undefined;
}

/** Throws on failure; callers roll back and show the "Progress not saved" banner. */
export async function saveProgress(userID: string, episode: EpisodeContent, progress: EpisodeProgress): Promise<void> {
  const row = progressToRow(userID, episode, progress);
  const { error } = await supabase().from('player_episode_progress').upsert(row, { onConflict: 'user_id,episode_id' });
  if (error) throw error;
  writeLocal(row);
}

export async function completedEpisodeIDs(userID: string): Promise<Set<string>> {
  const { data } = await supabase().from('player_episode_progress').select('episode_id').eq('user_id', userID).eq('solved', true);
  return new Set((data ?? []).map((r: { episode_id: string }) => r.episode_id));
}

export async function isPremium(userID: string): Promise<boolean> {
  const { data } = await supabase().from('premium_entitlements').select('is_premium,expires_at').eq('user_id', userID).limit(1).maybeSingle();
  if (!data?.is_premium) return false;
  return data.expires_at == null || new Date(data.expires_at as string) > new Date();
}
